import { useState } from 'react';
import {
  Box, Typography, Container, Paper, Button, Skeleton, Alert,
  Tabs, Tab,
} from '@mui/material';
import {
  CalendarMonth, ConfirmationNumber, Groups, Shield, Info,
  DoneAll, Circle,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient from '../../utils/httpClient';
import { colors } from '../../theme/theme';
import { useTranslation } from 'react-i18next';
import { queryKeys } from '../../utils/queryKeys';
import { timeAgo } from '../../utils/timeAgo';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  is_read: boolean;
  created_at: string;
}

const typeIcon: Record<string, any> = {
  event_confirmed: <CalendarMonth sx={{ color: colors.primary.main }} />,
  event_cancelled: <CalendarMonth color="error" />,
  voucher_purchased: <ConfirmationNumber sx={{ color: colors.primary.main }} />,
  voucher_refunded: <ConfirmationNumber color="warning" />,
  group_matched: <Groups sx={{ color: colors.secondary.main }} />,
  safety: <Shield color="error" />,
  system: <Info sx={{ color: colors.neutral[500] }} />,
};

async function fetchNotifications(signal?: AbortSignal): Promise<Notification[]> {
  const resp = await httpClient.get('/notifications/', { signal });
  return Array.isArray(resp.data) ? resp.data : resp.data?.notifications || [];
}

export default function NotificationsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  // Mutations stay local (`qc.setQueryData` optimistic mark-read) —
  // useMutation would add ceremony without a useful error path.
  const { data: notifications = [], isLoading: loading, error: queryError } = useQuery({
    queryKey: queryKeys.notifications.list(),
    queryFn: ({ signal }) => fetchNotifications(signal),
  });
  const error = queryError ? ((queryError as Error).message || 'Failed to load notifications') : '';

  const setNotifications = (updater: (prev: Notification[]) => Notification[]) => {
    qc.setQueryData<Notification[]>(queryKeys.notifications.list(), prev => updater(prev || []));
  };

  const markAsRead = async (id: string) => {
    try {
      await httpClient.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (err) {
      // PUT failed before any local update — the item stays unread in the UI.
      // Log for observability.
      console.warn('[NotificationsPage] mark-read PUT failed', err);
    }
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.is_read);
    await Promise.all(unread.map(n => httpClient.put(`/notifications/${n.id}/read`).catch(err => {
      // best-effort: failures are logged, but the blanket local mark-read
      // below still runs; the next refetch reconciles with the server.
      console.warn('[NotificationsPage] bulk mark-read PUT failed', err);
    })));
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const filtered = filter === 'unread' ? notifications.filter(n => !n.is_read) : notifications;
  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#FFFFFF' }}>{t('nav.notifications')}</Typography>
          {unreadCount > 0 && <Typography variant="body2" color="text.secondary">{unreadCount} unread</Typography>}
        </Box>
        {unreadCount > 0 && (
          <Button size="small" startIcon={<DoneAll />} onClick={markAllRead}>Mark all read</Button>
        )}
      </Box>

      <Tabs value={filter} onChange={(_, v) => setFilter(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}>
        <Tab label="All" value="all" />
        <Tab label={`Unread (${unreadCount})`} value="unread" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rounded" height={80} sx={{ borderRadius: 2 }} />)}
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Info sx={{ fontSize: 48, color: colors.neutral[300], mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {filter === 'unread' ? 'All caught up!' : 'No notifications yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {filter === 'unread' ? "You've read all your notifications" : 'Join an event to start receiving updates'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filtered.map(n => (
            <Paper key={n.id} onClick={() => !n.is_read && markAsRead(n.id)}
              sx={{
                p: 2, display: 'flex', gap: 2, alignItems: 'flex-start',
                cursor: n.is_read ? 'default' : 'pointer',
                bgcolor: n.is_read ? 'transparent' : colors.primary.subtle,
                border: `1px solid ${n.is_read ? colors.neutral[200] : colors.primary.light}`,
                borderRadius: 2, transition: 'all 0.2s',
                '&:hover': { bgcolor: colors.neutral[50] },
              }}>
              <Box sx={{ mt: 0.5 }}>{typeIcon[n.type] || typeIcon.system}</Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: n.is_read ? 400 : 700 }}>{n.title}</Typography>
                  {!n.is_read && <Circle sx={{ fontSize: 8, color: colors.primary.main }} />}
                </Box>
                <Typography variant="body2" color="text.secondary">{n.body}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {timeAgo(n.created_at)}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Container>
  );
}
